import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Observable, of } from "rxjs";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { LocalStorageKeys } from "../../../core/storage/local-storage";
import { Company } from "../../../modules/portal/companies/company.type";
import { Partner } from "../../../modules/portal/partners/partner.type";
import { UserService } from "../../../modules/portal/users/user.service";
import { UserTokens } from "../../types/app.type";
import { UserInfo } from "../../types/user-info";
import { DynamicFormComponent } from "../dynamic-form/dynamic-form.component";
import { QuestionBase } from "../dynamic-form/question-base";
import { NotificationService } from "../notification/notification.service";
import { ViewAs } from "./view-as";
import { ViewAsModalComponent } from "./view-as-modal/view-as-modal.component";
import { ViewAsService } from "./view-as.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-view-as",
  templateUrl: "./view-as.component.html",
  styleUrls: ["./view-as.component.css"],
})
export class ViewAsComponent implements OnInit {
  @ViewChild(DynamicFormComponent) form: DynamicFormComponent;

  currentUser: UserInfo;
  companyFilterForm: FormGroup;
  questions: QuestionBase<string | boolean>[];
  company: Company;
  partner: Partner;

  constructor(
    private authenticationService: AuthenticationService,
    private viewAsService: ViewAsService,
    private loader: LoadingService,
    private userService: UserService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  ngOnInit(): void {
    this.setCompany();
    this.setPartner();
    this.getQuestions();
  }

  resetFilter(): void {
    this.loader.show();
    this.viewAsService.clearLocalStorage();
    if (this.currentUser.isCompany()) {
      this.updateSelectedCompanyId(this.currentUser.id, {
        companyId: null,
      });
    } else {
      window.location.reload();
    }
  }

  isResetFilterVisible(): boolean {
    return !!localStorage.getItem(LocalStorageKeys.viewAs);
  }

  showFilterModal() {
    this.getQuestions();
    const dialogRef = this.dialog.open(ViewAsModalComponent, {
      width: "500px",
      panelClass: "custom-dialog-container",
      autoFocus: false,
      data: {
        questions: this.questions,
      },
    });
    dialogRef.componentInstance.submit.subscribe((payload: Partial<Pick<ViewAs, "companyId" | "partnerId">>) => {
      this.onSubmit(payload);
    });
    dialogRef.componentInstance.back.subscribe(() => {
      dialogRef.close();
    });
  }

  private getQuestions() {
    this.viewAsService
      .generateQuestions({
        ...(this.currentUser.isCompany() && {
          skipCompany: false,
          skipPartner: true,
          user: this.currentUser,
        }),
        ...(this.currentUser.isPartner() && {
          hidePartner: true,
        }),
        ...(this.currentUser.isGlobal() && {
          hidePartner: !!this.viewAsService.getViewAs()?.partnerId,
        }),
        partnerId: this.currentUser.partnerId || this.viewAsService.getViewAs()?.partnerId,
        companyId: this.viewAsService.getViewAs()?.partnerId,
      })
      .subscribe((qs) => {
        this.questions = qs;
        this.loader.hide();
      });
  }

  private async onSubmit(viewAs: Partial<Pick<ViewAs, "companyId" | "partnerId">>): Promise<void> {
    this.loader.show();
    if (this.currentUser.isCompany()) {
      this.updateSelectedCompanyId(this.currentUser.id, viewAs);
    } else {
      await this.setIdsLocalStorage(viewAs);
      window.location.reload();
    }
  }

  /** Set company. Used to persist and show company name */
  private setCompany(): void {
    const viewAs = this.viewAsService.getViewAs();
    this.company = viewAs?.company || this.currentUser.selectedCompany;
  }

  /** Set partner. Used to persist and show partner name */
  private setPartner(): void {
    const viewAs = this.viewAsService.getViewAs();
    this.partner = viewAs?.partner || (this.currentUser.isGlobal() ? viewAs?.company?.partner : null);
  }

  private setIdsLocalStorage(viewAs: Partial<Pick<ViewAs, "companyId" | "partnerId">>): Promise<void> {
    return this.viewAsService.saveInLocalStorage(viewAs);
  }

  private updateSelectedCompanyId(userId: string, viewAs: Partial<Pick<ViewAs, "companyId" | "partnerId">>): void {
    this.userService
      .updateUserProfile(userId, { selectedCompanyId: viewAs.companyId })
      .pipe(finalize(() => this.form.enable()))
      .subscribe(
        async () => {
          await this.setIdsLocalStorage(viewAs);
          const tokens: Observable<UserTokens> = of({
            accessToken: this.currentUser.accessToken,
            refreshToken: this.currentUser.refreshToken,
          });
          this.authenticationService.setSession(tokens, true).subscribe();
        },
        (err) => {
          const error = `There was an error updating the selected company:\n ${err}`;
          this.notificationService.error(error);
          this.loader.hide();
        },
      );
  }
}
